import isEqual from "react-fast-compare";
import { AsyncActionMetaInfo } from "../store/store-util";

export interface LoadableData<REQ, RESP, ERR = string> {
    loaded: boolean;
    params?: REQ;
    loading: boolean;
    data: RESP;
    error: boolean;
    e?: ERR;
}

export interface RequestPayload<P> {
    requestId: string;
    params: P;
    meta?: AsyncActionMetaInfo;
}

export interface SuccessPayload<P, V> {
    meta: AsyncActionMetaInfo;
    requestId: string;
    params: P;
    data: V;
    takeLatest: boolean;
}

export interface FailurePayload<P, V> {
    meta: AsyncActionMetaInfo;
    requestId: string;
    params: P;
    e: V;
    takeLatest: boolean;
}

export const initialData = <P, T, E = any>(data: T): LoadableData<P, T, E> => ({data, error: false, loading: false, loaded: false});

export const isSuccessfullyLoaded = <P, T, E>(data: LoadableData<P, T, E>): boolean => data.loaded && !data.error && !data.loading;

export const isFailure = <P, T, E>(data: LoadableData<P, T, E>): boolean => data.loaded && data.error && !data.loading;

export const shouldLoad = <P, T, E>(data: LoadableData<P, T, E>, request: P) => !isEqual(data.params, request);


export interface Filter<T = string> {
    filterField: string;
    value: T;
    matchMode: MatchMode;
}

export interface Filters {
    filters: Filter[];
}

export interface Count {
    count: number;
}

export interface WithBoolean {
    boolean: boolean;
}

export enum MatchMode {
    EQUALS = "EQUALS",
    IS_NULL = "IS_NULL",
    NEQ = "NEQ",
    I_LIKE = "I_LIKE",
    LTE = "LTE",
    LT = "LT",
    GTE = "GTE",
}

export interface PageRequest {
    page: number;
    size: number;
    sortField?: string;
    sortOrder?: number;
    filters?: Filter<any>[];
    debounce: number;
    showFilters?: boolean;
}

export interface SummaryRequest {
    field: string;
    filters: Filter<any>[];
}

export type With<T> = {
    [K in string]: T[]
}

export interface SummaryResponse {
    count: number;
    sum: number;
    avg: number;
    min: number;
    max: number;
}

export interface PageResponse<T> {
    "_links": {
        self?: {
            "href": string
        },
        first?: {
            "href": string
        },
        next?: {
            "href": string
        },
        previous?: {
            "href": string
        },
        last?: {
            "href": string
        },
    },
    "_embedded": With<T>
    "page": Page
}

export interface Page {
    "size": number,
    "totalElements": number,
    "totalPages": number,
    "number": number
}

export const getData = <T>(response?: PageResponse<T>): T[] => {
    if (response?._embedded) {
        const key = Object.keys(response._embedded)[0];
        if (key) {
            return response._embedded[key];
        }
    }
    return [];
};

export const emptyResponse = <T>(data: T[]): PageResponse<T> => ({
    _embedded: {initial: data},
    _links: {},
    page: {
        number: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0
    }
});

